import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, PencilLine, Plus, Trash2, Save, ImageIcon, X } from "lucide-react";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { ModulePanel } from "@/components/admin/AdminPrimitives";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { ClesApiPanel } from "@/components/admin/ClesApiPanel";
import { hasAnyRole } from "@/pages/admin/RequireAdmin";
import { useI18n } from "@/lib/i18n";
import { useParametres, useSetParametre } from "@/hooks/useParametres";
import { useUploadImage } from "@/hooks/useFileUpload";
import { usePays, useCreatePays, useUpdatePays, useDeletePays } from "@/hooks/usePays";
import {
  useTypesArticle,
  useCreateTypeArticle,
  useUpdateTypeArticle,
  useDeleteTypeArticle,
} from "@/hooks/useTypesArticle";
import type { PaysDto } from "@/types/pays.types";
import type { TypeArticleDto } from "@/types/type-article.types";

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

// ─── Identité du site (titre, logo, slogan) ──────────────────────────────────
function SiteIdentitySettings() {
  const { t } = useI18n();
  const { data: parametres, isLoading } = useParametres();
  const setParametre = useSetParametre();
  const uploadImage = useUploadImage();

  const [titre, setTitre] = useState("");
  const [slogan, setSlogan] = useState("");
  const [logo, setLogo] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    if (!parametres || loaded) return;
    setTitre(parametres["site.titre"] || "");
    setSlogan(parametres["site.slogan"] || "");
    setLogo(parametres["site.logo"] || "");
    setLoaded(true);
  }, [parametres, loaded]);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const result = await uploadImage.mutateAsync(file);
      setLogoError(false);
      setLogo(result.url);
    } catch {
      toast.error("Échec de l'envoi du logo");
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await Promise.all([
        setParametre.mutateAsync({ cle: "site.titre", valeur: titre }),
        setParametre.mutateAsync({ cle: "site.slogan", valeur: slogan }),
        setParametre.mutateAsync({ cle: "site.logo", valeur: logo }),
      ]);
      toast.success("Identité du site enregistrée");
    } catch {
      toast.error("Échec de l'enregistrement");
    }
  };

  return (
    <ModulePanel title={t("admin.settings.identity.title")} description={t("admin.settings.identity.desc")}>
      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Chargement…
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Titre du site</label>
              <input
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                placeholder="Ramba News"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Slogan</label>
              <input
                value={slogan}
                onChange={(e) => setSlogan(e.target.value)}
                placeholder="Information vérifiée. Sources protégées."
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Logo</label>
            {logo ? (
              <div className="flex items-center gap-3">
                {!logoError ? (
                  <img
                    src={logo}
                    alt="Logo du site"
                    onError={() => setLogoError(true)}
                    className="h-14 w-14 rounded-md border border-border object-contain bg-surface"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-md border border-dashed border-alert/40 bg-alert/5 text-alert">
                    <ImageIcon className="h-5 w-5" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => { setLogo(""); setLogoError(false); }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs text-alert hover:bg-surface"
                >
                  <X className="h-3.5 w-3.5" /> Retirer
                </button>
              </div>
            ) : (
              <label className="flex w-fit cursor-pointer items-center gap-2 rounded-md border border-dashed border-border bg-surface/40 px-4 py-3 text-muted-foreground hover:border-foreground hover:text-foreground hover:bg-surface transition-colors">
                {uploadImage.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                <span className="text-xs font-semibold">Choisir un logo</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploadImage.isPending} />
              </label>
            )}
          </div>
          <button
            type="submit"
            disabled={setParametre.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-60"
          >
            {setParametre.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
        </form>
      )}
    </ModulePanel>
  );
}

// ─── Footer & réseaux sociaux ─────────────────────────────────────────────────
function FooterSettings() {
  const { data: parametres, isLoading } = useParametres();
  const setParametre = useSetParametre();

  const [texte, setTexte] = useState("");
  const [facebook, setFacebook] = useState("");
  const [twitter, setTwitter] = useState("");
  const [instagram, setInstagram] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [youtube, setYoutube] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!parametres || loaded) return;
    setTexte(parametres["footer.texte"] || "");
    setFacebook(parametres["footer.reseaux.facebook"] || "");
    setTwitter(parametres["footer.reseaux.twitter"] || "");
    setInstagram(parametres["footer.reseaux.instagram"] || "");
    setLinkedin(parametres["footer.reseaux.linkedin"] || "");
    setYoutube(parametres["footer.reseaux.youtube"] || "");
    setWhatsapp(parametres["footer.reseaux.whatsapp"] || "");
    setLoaded(true);
  }, [parametres, loaded]);

  const champs: [string, string, (v: string) => void][] = [
    ["footer.texte", texte, setTexte],
    ["footer.reseaux.facebook", facebook, setFacebook],
    ["footer.reseaux.twitter", twitter, setTwitter],
    ["footer.reseaux.instagram", instagram, setInstagram],
    ["footer.reseaux.linkedin", linkedin, setLinkedin],
    ["footer.reseaux.youtube", youtube, setYoutube],
    ["footer.reseaux.whatsapp", whatsapp, setWhatsapp],
  ];

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await Promise.all(champs.map(([cle, valeur]) => setParametre.mutateAsync({ cle, valeur })));
      toast.success("Paramètres du footer enregistrés");
    } catch {
      toast.error("Échec de l'enregistrement");
    }
  };

  return (
    <ModulePanel title="Footer & réseaux sociaux" description="Contenu affiché en pied de page du site public">
      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Chargement…
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Texte du footer</label>
            <textarea
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Facebook</label>
              <input value={facebook} onChange={(e) => setFacebook(e.target.value)} placeholder="https://facebook.com/…" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Twitter / X</label>
              <input value={twitter} onChange={(e) => setTwitter(e.target.value)} placeholder="https://x.com/…" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Instagram</label>
              <input value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="https://instagram.com/…" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">LinkedIn</label>
              <input value={linkedin} onChange={(e) => setLinkedin(e.target.value)} placeholder="https://linkedin.com/company/…" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">YouTube</label>
              <input value={youtube} onChange={(e) => setYoutube(e.target.value)} placeholder="https://youtube.com/…" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">WhatsApp</label>
              <input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="https://wa.me/…" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
          </div>
          <button
            type="submit"
            disabled={setParametre.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-60"
          >
            {setParametre.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
        </form>
      )}
    </ModulePanel>
  );
}

// ─── Pages statiques (À propos, Charte éditoriale, Confidentialité, Conditions) ──
function PagesSettings() {
  const { data: parametres, isLoading } = useParametres();
  const setParametre = useSetParametre();

  const PAGES = [
    { cle: "pages.about", label: "À propos" },
    { cle: "pages.editorial", label: "Charte éditoriale" },
    { cle: "pages.privacy", label: "Confidentialité" },
    { cle: "pages.terms", label: "Conditions" },
    { cle: "pages.aiCharter", label: "Charte d'utilisation de l'IA" },
  ];

  const [valeurs, setValeurs] = useState<Record<string, { titre: string; corps: string }>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!parametres || loaded) return;
    const init: Record<string, { titre: string; corps: string }> = {};
    PAGES.forEach((p) => {
      init[p.cle] = {
        titre: parametres[`${p.cle}.titre`] || "",
        corps: parametres[`${p.cle}.corps`] || "",
      };
    });
    setValeurs(init);
    setLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametres, loaded]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await Promise.all(
        PAGES.flatMap((p) => [
          setParametre.mutateAsync({ cle: `${p.cle}.titre`, valeur: valeurs[p.cle]?.titre ?? "" }),
          setParametre.mutateAsync({ cle: `${p.cle}.corps`, valeur: valeurs[p.cle]?.corps ?? "" }),
        ])
      );
      toast.success("Pages enregistrées");
    } catch {
      toast.error("Échec de l'enregistrement");
    }
  };

  return (
    <ModulePanel title="Pages (À propos, Charte éditoriale, Confidentialité, Conditions)" description="Titre et texte affichés sur ces pages publiques">
      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Chargement…
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-5">
          {PAGES.map((p) => (
            <div key={p.cle} className="space-y-2 rounded-md border border-border p-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-foreground">{p.label}</div>
              <input
                value={valeurs[p.cle]?.titre ?? ""}
                onChange={(e) => setValeurs((v) => ({ ...v, [p.cle]: { ...v[p.cle], titre: e.target.value } }))}
                placeholder="Titre"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
              <textarea
                value={valeurs[p.cle]?.corps ?? ""}
                onChange={(e) => setValeurs((v) => ({ ...v, [p.cle]: { ...v[p.cle], corps: e.target.value } }))}
                placeholder="Texte"
                rows={4}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
              />
            </div>
          ))}
          <button
            type="submit"
            disabled={setParametre.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-60"
          >
            {setParametre.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
        </form>
      )}
    </ModulePanel>
  );
}

// ─── Pays ──────────────────────────────────────────────────────────────────────
function PaysSettings() {
  const { data: pays, isLoading } = usePays();
  const createPays = useCreatePays();
  const updatePays = useUpdatePays();
  const deletePays = useDeletePays();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [slug, setSlug] = useState("");
  const [nom, setNom] = useState("");
  const [nomEn, setNomEn] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<PaysDto | null>(null);

  const paysList = useMemo(() => (Array.isArray(pays) ? pays : []), [pays]);

  const resetForm = () => {
    setEditingId(null);
    setSlug("");
    setNom("");
    setNomEn("");
  };

  const startEdit = (p: PaysDto) => {
    setEditingId(p.id);
    setSlug(p.slug || "");
    setNom(p.nom || "");
    setNomEn(p.nomEn || "");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { slug: slug || slugify(nom || nomEn), nom, nomEn, actif: true };
    if (!payload.slug || !payload.nom) return;

    if (editingId) {
      updatePays.mutate({ id: editingId, payload }, {
        onSuccess: resetForm,
        onError: (e: any) => toast.error(e?.response?.data?.message || "Échec de la modification"),
      });
    } else {
      createPays.mutate(payload, {
        onSuccess: resetForm,
        onError: (e: any) => toast.error(e?.response?.data?.message || "Échec de la création"),
      });
    }
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deletePays.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: (e: any) => {
        toast.error(e?.response?.data?.message || "Impossible de supprimer ce pays");
        setDeleteTarget(null);
      },
    });
  };

  return (
    <ModulePanel title="Pays" description="Pays disponibles pour les articles et le filtre public">
      <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-3 mb-4">
        <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="Slug (optionnel)" className="rounded-md border border-border bg-background px-3 py-2 text-sm" />
        <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Nom (FR)" className="rounded-md border border-border bg-background px-3 py-2 text-sm" />
        <input value={nomEn} onChange={(e) => setNomEn(e.target.value)} placeholder="Nom (EN)" className="rounded-md border border-border bg-background px-3 py-2 text-sm" />
        <div className="sm:col-span-3 flex items-center gap-2">
          <button
            type="submit"
            disabled={createPays.isPending || updatePays.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background"
          >
            {(createPays.isPending || updatePays.isPending) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {editingId ? "Enregistrer" : "Ajouter"}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="rounded-md border border-border px-4 py-2 text-sm">Annuler</button>
          )}
        </div>
      </form>

      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Chargement…
        </div>
      ) : paysList.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Aucun pays enregistré</div>
      ) : (
        <div className="space-y-2">
          {paysList.map((p: PaysDto) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface/50 px-3 py-2.5">
              <div>
                <div className="font-medium text-foreground">{p.nom} {p.nomEn ? `/ ${p.nomEn}` : ""}</div>
                <div className="text-xs text-muted-foreground">slug: {p.slug}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(p)} className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs">
                  <PencilLine className="h-3.5 w-3.5" /> Modifier
                </button>
                <button onClick={() => setDeleteTarget(p)} className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs text-alert">
                  <Trash2 className="h-3.5 w-3.5" /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Supprimer ce pays ?"
        description={deleteTarget ? `« ${deleteTarget.nom} » sera définitivement supprimé.` : undefined}
        onConfirm={confirmDelete}
        loading={deletePays.isPending}
      />
    </ModulePanel>
  );
}

// ─── Types d'article ────────────────────────────────────────────────────────────
function TypesArticleSettings() {
  const { data: types, isLoading } = useTypesArticle();
  const createType = useCreateTypeArticle();
  const updateType = useUpdateTypeArticle();
  const deleteType = useDeleteTypeArticle();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [code, setCode] = useState("");
  const [libelle, setLibelle] = useState("");
  const [libelleEn, setLibelleEn] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<TypeArticleDto | null>(null);

  const typeList = useMemo(() => (Array.isArray(types) ? types : []), [types]);

  const resetForm = () => {
    setEditingId(null);
    setCode("");
    setLibelle("");
    setLibelleEn("");
  };

  const startEdit = (ta: TypeArticleDto) => {
    setEditingId(ta.id);
    setCode(ta.code || "");
    setLibelle(ta.libelle || "");
    setLibelleEn(ta.libelleEn || "");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { code: code || slugify(libelle).toUpperCase().replace(/-/g, "_"), libelle, libelleEn, actif: true };
    if (!payload.code || !payload.libelle) return;

    if (editingId) {
      updateType.mutate({ id: editingId, payload }, {
        onSuccess: resetForm,
        onError: (e: any) => toast.error(e?.response?.data?.message || "Échec de la modification"),
      });
    } else {
      createType.mutate(payload, {
        onSuccess: resetForm,
        onError: (e: any) => toast.error(e?.response?.data?.message || "Échec de la création"),
      });
    }
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteType.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: (e: any) => {
        toast.error(e?.response?.data?.message || "Impossible de supprimer ce type");
        setDeleteTarget(null);
      },
    });
  };

  return (
    <ModulePanel title="Types d'article" description="Types disponibles lors de la création d'un article">
      <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-3 mb-4">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Code (ex: ARTICLE)" className="rounded-md border border-border bg-background px-3 py-2 text-sm font-mono" />
        <input value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="Libellé (FR)" className="rounded-md border border-border bg-background px-3 py-2 text-sm" />
        <input value={libelleEn} onChange={(e) => setLibelleEn(e.target.value)} placeholder="Libellé (EN)" className="rounded-md border border-border bg-background px-3 py-2 text-sm" />
        <div className="sm:col-span-3 flex items-center gap-2">
          <button
            type="submit"
            disabled={createType.isPending || updateType.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background"
          >
            {(createType.isPending || updateType.isPending) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {editingId ? "Enregistrer" : "Ajouter"}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="rounded-md border border-border px-4 py-2 text-sm">Annuler</button>
          )}
        </div>
      </form>

      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Chargement…
        </div>
      ) : typeList.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Aucun type enregistré</div>
      ) : (
        <div className="space-y-2">
          {typeList.map((ta: TypeArticleDto) => (
            <div key={ta.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface/50 px-3 py-2.5">
              <div>
                <div className="font-medium text-foreground">{ta.libelle} {ta.libelleEn ? `/ ${ta.libelleEn}` : ""}</div>
                <div className="text-xs text-muted-foreground font-mono">code: {ta.code}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(ta)} className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs">
                  <PencilLine className="h-3.5 w-3.5" /> Modifier
                </button>
                <button onClick={() => setDeleteTarget(ta)} className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs text-alert">
                  <Trash2 className="h-3.5 w-3.5" /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Supprimer ce type d'article ?"
        description={deleteTarget ? `« ${deleteTarget.libelle} » sera définitivement supprimé.` : undefined}
        onConfirm={confirmDelete}
        loading={deleteType.isPending}
      />
    </ModulePanel>
  );
}

function Settings() {
  const { t } = useI18n();

  const SECTIONS = [
    { title: t("admin.settings.languages.title"), body: t("admin.settings.languages.desc") },
    { title: t("admin.settings.categories.title"), body: t("admin.settings.categories.desc") },
    { title: t("admin.settings.workflow.title"), body: t("admin.settings.workflow.desc") },
    { title: t("admin.settings.notifications.title"), body: t("admin.settings.notifications.desc") },
    { title: t("admin.settings.security.title"), body: t("admin.settings.security.desc") },
    { title: t("admin.settings.cms.title"), body: t("admin.settings.cms.desc") },
  ];

  return (
    <>
      <AdminTopbar title={t("admin.nav.settings")} subtitle={t("admin.settings.subtitle")} />
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <SiteIdentitySettings />
        <FooterSettings />
        <PagesSettings />
        <PaysSettings />
        <TypesArticleSettings />
        {/* Clés d'accès de la newsroom IA (ChatGPT) — gestion réservée à l'admin. */}
        {hasAnyRole(["ADMIN"]) && <ClesApiPanel />}

        <div className="grid gap-4 md:grid-cols-2">
          {SECTIONS.map((s) => (
            <ModulePanel key={s.title} title={s.title} description={s.body}>
              <div className="text-xs text-muted-foreground">{t("admin.settings.configurable")}</div>
            </ModulePanel>
          ))}
        </div>
      </div>
    </>
  );
}

export default Settings;
